import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Lock, Globe, Trash2, Settings2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import ViewToggle from '../components/board/ViewToggle';
import KanbanBoard from '../components/board/KanbanBoard';
import ListView from '../components/board/ListView';
import CalendarView from '../components/board/CalendarView';
import BoardForm from '../components/board/BoardForm';
import ColumnManager from '../components/board/ColumnManager';
import ExportMenu from '../components/board/ExportMenu';
import NewTaskForm from '../components/board/NewTaskForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Badge from '../components/common/Badge';

export default function BoardPage() {
  const { state, dispatch, getBoardById, apiDeleteBoard } = useApp();
  const [searchParams] = useSearchParams();
  const boardIdFromUrl = searchParams.get('boardId');

  const [showNewBoard, setShowNewBoard] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    if (boardIdFromUrl && state.boards.length > 0) {
      if (boardIdFromUrl !== state.currentBoardId && state?.boards?.find(b => b.id === boardIdFromUrl)) {
        dispatch({ type: 'SET_BOARD', payload: boardIdFromUrl });
      }
    }
  }, [boardIdFromUrl, state.currentBoardId, state.boards, dispatch]);

  const board = getBoardById(state.currentBoardId);

  if (!board) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-sm text-slate-400">Nenhum quadro selecionado</p>
        <button
          onClick={() => setShowNewBoard(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 transition-colors"
        >
          <Plus size={16} />
          Criar Quadro
        </button>
        {showNewBoard && <BoardForm onClose={() => setShowNewBoard(false)} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 md:px-6 pt-4 md:pt-6 pb-2">
        <div className="flex items-center gap-3">
          {/*<select
            value={state.currentBoardId}
            onChange={(e) => dispatch({ type: 'SET_BOARD', payload: e.target.value })}
            className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-300"
            aria-label="Selecionar quadro"
          >
            {state.boards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>*/}
          <Badge className={board.visibility === 'private' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}>
            {board.visibility === 'private' ? (
              <span className="flex items-center gap-1"><Lock size={10} /> Privado</span>
            ) : (
              <span className="flex items-center gap-1"><Globe size={10} /> Público</span>
            )}
          </Badge>
          <span className="text-xs text-slate-400 hidden lg:inline">{board.description}</span>
        </div>
        <div className="flex items-center gap-2">
          <ViewToggle />
          <ExportMenu boardId={board.id} />
          <button
            onClick={() => setShowColumnManager(true)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-brand-500 hover:bg-brand-50 transition-colors"
            aria-label="Gerenciar colunas"
          >
            <Settings2 size={16} />
          </button>
          <button
            onClick={() => setShowNewTask(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-white bg-brand-500 hover:bg-brand-600 transition-colors shadow-sm"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Criar Tarefa</span>
          </button>
          <button
            onClick={() => setShowNewBoard(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 transition-colors"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Novo Quadro</span>
          </button>
          {state.boards.length >= 1 && (
            <button
              onClick={() => setDeleteConfirm(true)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              aria-label="Excluir quadro"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {state.currentView === 'kanban' && <KanbanBoard board={board} />}
        {state.currentView === 'list' && <ListView board={board} />}
        {state.currentView === 'calendar' && <CalendarView board={board} />}
      </div>

      {showNewBoard && <BoardForm onClose={() => setShowNewBoard(false)} />}
      {showNewTask && (
        <NewTaskForm
          boardId={board.id}
          initialStatus={board.columns[0]?.status || 'backlog'}
          onClose={() => setShowNewTask(false)}
        />
      )}
      {showColumnManager && <ColumnManager board={board} onClose={() => setShowColumnManager(false)} />}

      <ConfirmDialog
        isOpen={deleteConfirm}
        title="Excluir quadro"
        message={`Tem certeza que deseja excluir o quadro "${board.name}"? Todas as tarefas associadas permanecerão no sistema.`}
        confirmLabel="Excluir"
        onConfirm={async () => {
          await apiDeleteBoard(board.id);
          setDeleteConfirm(false);
        }}
        onCancel={() => setDeleteConfirm(false)}
      />
    </div>
  );
}
